import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { SubscriptionList } from '@vendasta/rx-utils';
import { EffortAggregate, SalespeopleService } from '@vendasta/sales-v2';
import { BehaviorSubject, combineLatest, firstValueFrom, iif, Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { OpportunityTypeFilterStoreService } from '../../filters/opportunity-type-filter/opportunity-type-filter-store.service';
import { PipelineField, PipelineSort, SortDirection } from '../pipeline-sort';
import { PAGE_SIZE, PipelineTableService } from '../pipeline-table-service/pipeline-table.service';
import { OpportunityRow } from '../pipeline-table-service/pipeline-table.service.interface';
import { UserPipelines } from '../user-pipelines/user-pipelines';
import { PIPELINE_TABLE_SERVICE_CONFIG_TOKEN, PipelineTableServiceConfig } from '../pipeline-table-service/tokens';

export type OpportunityId = string;
export type EffortType = string;
interface MultiOpportunityEfforts {
  [key: string]: EffortAggregate;
}

@Component({
  selector: 'sales-ui-pipeline-table',
  templateUrl: './pipeline-table.component.html',
  styleUrls: ['./pipeline-table.component.scss'],
  standalone: false,
})
export class PipelineTableComponent implements OnDestroy {
  private readonly subscriptions = SubscriptionList.new();
  public readonly opportunities$: Observable<OpportunityRow[]>;
  public readonly totalItems$: Observable<number>;

  public readonly COLUMNS = PipelineField;

  public readonly sortDir$ = this.pipelineTableService.sort$.pipe(
    map((s) => (s?.isAscending ? SortDirection.ASC : SortDirection.DESC)),
  );
  public readonly sortActive$ = this.pipelineTableService.sort$.pipe(
    map((sort) => sort?.active),
    startWith(this.COLUMNS.POTENTIAL),
  );

  readonly pageIndex$: Observable<number>;

  public readonly displayedColumns$;

  public readonly displayedOpportunities$: Observable<OpportunityRow[]>;

  private readonly currentPageStartIndex$$ = new BehaviorSubject<number>(0);
  readonly currentPageStartIndex$ = this.currentPageStartIndex$$.asObservable();

  public readonly PAGE_SIZE = PAGE_SIZE;

  @ViewChild('paginator', { static: false }) readonly paginator: MatPaginator;

  readonly loadingOpportunities$: Observable<boolean>;
  readonly loadingError$: Observable<boolean>;

  readonly disablePagination$: Observable<boolean>;

  readonly opportunityMetaTemplateData$: Observable<{ preferredCurrency: string; efforts: MultiOpportunityEfforts }>;

  readonly CLOSEDWON = 'closed-won';
  readonly CLOSEDLOST = 'closed-lost';
  userPipelines$ = this.userPipelines.partnerPipelines$;
  availableTypes$ = this.opportunityTypeStoreService.availableTypes$;

  hasCompanyDetailsUrl = !!this.pipelineConfig.getCompanyDetailsUrl;

  constructor(
    private readonly pipelineTableService: PipelineTableService,
    private readonly userPipelines: UserPipelines,
    private readonly effortsGetter: SalespeopleService,
    private readonly opportunityTypeStoreService: OpportunityTypeFilterStoreService,
    private readonly activatedRoute: ActivatedRoute,
    @Inject(PIPELINE_TABLE_SERVICE_CONFIG_TOKEN) private readonly pipelineConfig: PipelineTableServiceConfig,
  ) {
    this.loadingOpportunities$ = this.pipelineTableService.loading$;
    this.loadingError$ = this.pipelineTableService.listOpportunityError$;

    this.opportunities$ = this.pipelineTableService.opportunities$;
    this.totalItems$ = this.pipelineTableService.totalItems$;

    this.displayedOpportunities$ = combineLatest([this.opportunities$, this.currentPageStartIndex$]).pipe(
      map(([opps, pageIndex]) => {
        return opps.slice(pageIndex * PAGE_SIZE, PAGE_SIZE * (pageIndex + 1));
      }),
    );

    const preferredCurrency$: Observable<string> = this.pipelineTableService.preferredCurrency$;
    const opportunityEfforts$: Observable<MultiOpportunityEfforts> = this.displayedOpportunities$.pipe(
      map((opps: OpportunityRow[]): string[] => (opps || []).map((o) => o.id)),
      switchMap((ids: string[]): Observable<{ [key: string]: EffortAggregate }> => {
        return iif(() => ids.length > 0, this.effortsGetter.getMultiOpportunitySalespersonEfforts(ids), of({}));
      }),
      startWith({}),
    );
    this.opportunityMetaTemplateData$ = combineLatest([preferredCurrency$, opportunityEfforts$]).pipe(
      map(([c, e]) => ({ preferredCurrency: c, efforts: e })),
    );

    this.disablePagination$ = combineLatest([this.loadingOpportunities$, this.loadingError$]).pipe(
      map(([loading, error]) => loading || error),
    );

    this.subscriptions.add(this.pipelineTableService.resetToFirstPage$, () => {
      if (this.paginator) {
        this.paginator.firstPage();
      }
    });

    this.subscriptions.add(this.pipelineTableService.setEnabledColumns$());

    this.displayedColumns$ = combineLatest([
      this.opportunityMetaTemplateData$,
      this.pipelineTableService.enabledColumns$,
    ]).pipe(map(([, columns]) => columns));

    this.pipelineTableService.applyFiltersFromQueryParams();
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  sortChanged(sort: Sort): void {
    const s = <PipelineSort>{
      active: sort.active as PipelineField,
      isAscending: sort.direction === SortDirection.ASC,
    };

    this.pipelineTableService.applySorting(s);
  }

  async pageUpdated(event: PageEvent): Promise<void> {
    const oppsLength = (await firstValueFrom(this.opportunities$)).length;

    // Check if they went forward a page
    if (event.pageIndex > event.previousPageIndex) {
      const maxItemIndex = event.pageIndex * event.pageSize + (event.pageSize - 1);

      if (maxItemIndex >= oppsLength - 1) {
        this.pipelineTableService.getNextPage().then(() => {
          this.currentPageStartIndex$$.next(event.pageIndex);
        });
      } else {
        this.currentPageStartIndex$$.next(event.pageIndex);
      }
    } else {
      this.currentPageStartIndex$$.next(event.pageIndex);
    }
  }

  getCompanyDetailsUrl(id: string): string {
    return this.pipelineConfig.getCompanyDetailsUrl(id);
  }
}
